import React, { useEffect, useState } from 'react';

/**
 * Splash screen shown for ~1.2s on first paint.
 * Replaces the white flash with a premium maroon entrance featuring a
 * gold Om mark, brand name, and ornamental flourishes — Spotify-style
 * "professional product" feel without delaying the user beyond ~1s.
 *
 * Auto-dismisses, animates out, then unmounts entirely.
 */
const SplashScreen = () => {
  const [phase, setPhase] = useState('show');   // 'show' | 'fading' | 'gone'

  useEffect(() => {
    // Respect reduced-motion: dismiss instantly
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setPhase('gone');
      return;
    }
    const t1 = setTimeout(() => setPhase('fading'), 1100);
    const t2 = setTimeout(() => setPhase('gone'), 1700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (phase === 'gone') return null;

  return (
    <div
      aria-hidden="true"
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-500 ${
        phase === 'fading' ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        background:
          'radial-gradient(ellipse at center, #73111E 0%, #4A0912 60%, #2a050d 100%)',
        pointerEvents: phase === 'fading' ? 'none' : 'auto',
      }}
    >
      {/* mandala spin behind */}
      <div className="absolute inset-0 bg-mandala-large bg-center bg-no-repeat opacity-20 pointer-events-none" />

      {/* Centerpiece */}
      <div className="relative flex flex-col items-center text-center">
        {/* outer halo rings */}
        <div className="relative w-32 h-32 sm:w-40 sm:h-40 mb-5">
          <span className="absolute inset-0 rounded-full border border-brand-gold/30 animate-ping" />
          <span className="absolute inset-3 rounded-full border border-brand-gold/40" />
          <span className="absolute inset-6 rounded-full border border-brand-gold/55" />
          <div
            className="absolute inset-0 flex items-center justify-center font-hindi-serif text-7xl sm:text-8xl text-brand-gold-light"
            style={{
              textShadow: '0 0 30px rgba(212,175,55,0.6)',
              animation: 'splash-pop 800ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            }}
          >
            ॐ
          </div>
        </div>

        {/* Gold ornament + name */}
        <div className="flex items-center gap-3">
          <span className="w-12 h-px bg-gradient-to-r from-transparent to-brand-gold" />
          <span className="text-brand-gold-light text-[10px] uppercase tracking-[0.4em] font-semibold">
            Karma Kundali
          </span>
          <span className="w-12 h-px bg-gradient-to-l from-transparent to-brand-gold" />
        </div>

        {/* tag-line */}
        <p
          className="font-playfair italic text-brand-gold/85 text-sm sm:text-base mt-3"
          style={{ animation: 'splash-fade 700ms 200ms ease-out backwards' }}
        >
          By Astro Avinash
        </p>
      </div>

      <style>{`
        @keyframes splash-pop {
          0%   { opacity: 0; transform: scale(0.4); }
          60%  { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes splash-fade {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
